// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { BackButton } from '@advisor-app/components';
import { AppIcon, Tabs, type KpiBoxesProps } from '@shared/ui-kit/components';
import { Trades } from '@advisor-app/features/trades';
import { PageContainer } from '@shared/ui-kit/components/page-container';
import { formatToMoney } from '@advisor-app/features/utils/format-to-money';
import { useAppNavigate } from '@advisor-app/hooks';
import { useMultiAssetBacktest } from '@shared/portfolio-analytics/components/backtest';
import { useAppDispatch, useAppSelector } from '@advisor-app/store/store';
import { selectTradesTable } from '@shared/data-access/slices/portfolio-positions';
import {
  RebalanceTabs,
  renderColumnsTradesRebalance,
  useRunRebalance,
} from '@advisor-app/features/rebalance';
import { useSendToTrader } from '@advisor-app/features/operations';
import { useAdvisorRole } from '@advisor-app/hooks/use-advisor-role';
import { type FC, useCallback, useMemo } from 'react';
import { OperationType } from '@shared/data-access/interfaces/operation-type';
import {
  selectHistoricalWindow,
  selectRebalanceConfig,
} from '@advisor-app/store/slices/parameters/parameters-selectors';
import { HIST_VALUES, PeriodPicker } from '@shared/ui-kit/components/period-picker';
import { setHistoricalWindow } from '@advisor-app/store/slices/parameters';
import { MultiAsset } from '@shared/portfolio-analytics/components/backtest';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { useTranslation } from 'react-i18next';

export const RebalanceSummary: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();

  const { t } = useTranslation();

  const trades = useAppSelector(selectTradesTable);

  const {
    handleDeleteOption: deleteByIndex,
    resetRunRebalance,
    tax,
    target,
    isLoading,
    currentPortfolio,
    targetPortfolio,
  } = useRunRebalance();

  const { sendRebalanceTickers } = useSendToTrader();
  const { canUseTrader } = useAdvisorRole();
  const handleDeleteOption = useCallback(
    async (ticker: string, index?: number) => {
      await deleteByIndex(index!);
    },
    [deleteByIndex],
  );

  const handleSendToTrader = useCallback(async () => {
    if (!canUseTrader) return;

    try {
      await sendRebalanceTickers();

      resetRunRebalance();

      navigate('/operations');
    } catch (error) {
      /* istanbul ignore next */
      console.error('[RebalanceSummary]: handleSendToTrader, ', error);
    }
  }, [canUseTrader, navigate, resetRunRebalance, sendRebalanceTickers]);

  const buy = trades?.filter(trade => trade.operation === OperationType.BUY);
  /* istanbul ignore next */
  const totalBuy = buy?.reduce((prev, curr) => prev + curr.volume, 0);

  const sell = trades?.filter(trade => trade.operation === OperationType.SELL);

  /* istanbul ignore next */
  const totalSell = sell?.reduce((prev, curr) => prev + curr.volume, 0);

  const transfer = trades?.filter(
    trade => trade.operation === OperationType.TRANSFER,
  );
  /* istanbul ignore next */
  const totalTransfer = transfer?.reduce((prev, curr) => prev + curr.volume, 0);

  const hasOnlyOneData = useMemo(() => trades.length === 1, [trades]);

  const kpiData: KpiBoxesProps[] = [
    {
      value: formatToMoney(totalBuy - totalSell),
      text: t('glossary.kpi-net-new-investment', { ns: 'glossary' }),
      numberPositions: trades?.length,
      icon: 'TrendingUp',
      color: 'main',
    },
    {
      value: formatToMoney(totalBuy),
      text: t('glossary.kpi-buy', { ns: 'glossary' }),
      numberPositions: buy?.length,
      icon: 'CircleArrowUp',
      color: 'success',
    },
    {
      value: formatToMoney(totalSell),
      text: t('glossary.kpi-sell', { ns: 'glossary' }),
      numberPositions: sell?.length,
      icon: 'CircleArrowDown',
      color: 'danger',
    },
    {
      value: formatToMoney(totalTransfer),
      text: t('glossary.kpi-transfer', { ns: 'glossary' }),
      numberPositions: transfer?.length,
      icon: 'ArrowLeftRight',
      color: 'secondary',
    },
  ];

  const hist = useAppSelector(selectHistoricalWindow);

  const defaultHist = useMemo(() => HIST_VALUES.get(hist), [hist]);

  const setHist = useCallback(
    (value: number) => dispatch(setHistoricalWindow(value)),
    [dispatch],
  );

  const rebalance = useAppSelector(selectRebalanceConfig);

  const { amount: rebalanceCash, type: rebalanceType } = rebalance;

  const totalInvestment = useMemo(
    () => currentPortfolio.reduce((prev, curr) => prev + curr.qty * curr.price, 0),
    [currentPortfolio],
  );

  const targetInvestment = useMemo(
    () =>
      rebalanceType === 'withdrawal'
        ? totalInvestment - rebalanceCash
        : totalInvestment + rebalanceCash,
    [rebalanceCash, rebalanceType, totalInvestment],
  );

  const positions = useMemo(
    () =>
      targetPortfolio.map(position => {
        const price = position.details?.price ?? 1;

        return {
          price: price,
          ticker: position.details?.ticker ?? position.ticker,
          qty: (targetInvestment / price) * (position.weight / 100),
          unrealized_capital_gains: 0,
        };
      }),
    [targetInvestment, targetPortfolio],
  );

  const benchmark = useMemo(
    () =>
      currentPortfolio.map(position => ({
        ticker: position.details?.ticker ?? position.ticker,
        weight: (position.qty * position.price) / totalInvestment,
      })),
    [currentPortfolio, totalInvestment],
  );

  const multiAssetBacktesting = useMultiAssetBacktest({
    holdings: positions,
    hist: hist,
    benchmark,
  });

  return (
    <PageContainer
      //showBottomButtons
      actionBtn={
        <BackButton to="/rebalance/configuration" data-testid="btn-back" />
      }
      loading={
        isLoading ||
        multiAssetBacktesting.isFetchingBacktesting ||
        multiAssetBacktesting.isFetchingBacktesting
      }
    >
      <Tabs.Root defaultValue="trades" state-id="rebalance-summary">
        <Tabs.List>
          <Tabs.Trigger value="trades">
            <Translation i18nKey="rebalance.tabs.trades" ns="rebalance" />
          </Tabs.Trigger>
          {!!trades?.length && (
            <Tabs.Trigger value="tax">
              <Translation i18nKey="rebalance.tabs.tax" ns="rebalance" />
            </Tabs.Trigger>
          )}
          <Tabs.Trigger value="backtest">
            <Translation i18nKey="rebalance.tabs.backtest" ns="rebalance" />
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="trades">
          <Trades
            tableColumns={renderColumnsTradesRebalance}
            columnMethods={{
              tickerMethod: handleDeleteOption,
              hasOnlyOneData: hasOnlyOneData,
            }}
            tableData={trades}
            kpisDataArray={kpiData}
            handleSendToTrader={handleSendToTrader}
            excelFileName="trades-rebalance"
            showSendToTrader={canUseTrader}
            showDownload
            state-id="trades-rebalance-table"
            className="mt-12"
          />
        </Tabs.Content>

        {!!trades?.length && (
          <Tabs.Content value="tax" className="mt-[69px]">
            <RebalanceTabs.Tax
              tax={tax}
              deviation={target}
              handleDeleteOption={handleDeleteOption}
              rebalanceTableData={trades}
            />
          </Tabs.Content>
        )}
        <Tabs.Content value="backtest">
          <div className="col-span-2 mt-8 grid grid-cols-2 gap-y-8">
            <div className="col-span-2 flex">
              <PeriodPicker defaultValue={defaultHist} onValueChange={setHist} />
            </div>

            {multiAssetBacktesting.isError ? (
              <div
                className="col-span-2 mt-4 grid min-h-32 animate-fade place-items-center  items-center justify-center rounded-lg bg-section p-12"
                data-testid="error"
              >
                <AppIcon icon="Warning" size="2xl" color="danger" />

                <p className="text-lg font-semibold text-primary-medium">
                  {t('errors.generic-fetch-error', { ns: 'errors' })}
                </p>
              </div>
            ) : (
              !multiAssetBacktesting.isLoadingBacktesting && (
                <div className="col-span-2 grid grid-flow-row gap-y-8">
                  <MultiAsset
                    kpis={multiAssetBacktesting.kpis}
                    returns={multiAssetBacktesting.returns}
                    analysis={multiAssetBacktesting.analysis}
                    backtest={multiAssetBacktesting.backtest}
                    feature="rebalance"
                  />
                </div>
              )
            )}
          </div>
        </Tabs.Content>
      </Tabs.Root>
    </PageContainer>
  );
};

export default RebalanceSummary;
